import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getAuthHeaders } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  BookOpen, 
  Clock, 
  Quote, 
  Lightbulb, 
  Sparkles, 
  ArrowRight, 
  Check,
  Menu,
  X,
  Play
} from 'lucide-react';
import { toast } from 'sonner';
import jonathanPhoto from '../assets/jonathan-photo.jpg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const navigate = useNavigate();
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, { headers: { ...getAuthHeaders() } });
        setIsLoggedIn(response.ok);
      } catch {
        setIsLoggedIn(false);
      }
    };
    checkAuth();
  }, []);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    
    if (!youtubeUrl.trim()) {
      toast.error('Please enter a YouTube URL');
      return;
    }
    
    if (!isLoggedIn) {
      navigate('/signup');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API}/sermons/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ youtube_url: youtubeUrl })
      });
      
      if (response.ok) {
        const data = await response.json();
        navigate(`/results/${data.analysis_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to analyze sermon');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: <Sparkles className="w-6 h-6" strokeWidth={1.5} />,
      title: 'AI-Powered Summary',
      description: 'Get a comprehensive summary of any sermon in seconds, powered by advanced AI.'
    },
    {
      icon: <BookOpen className="w-6 h-6" strokeWidth={1.5} />,
      title: 'Scripture Detection',
      description: 'Automatically extract and organize every Bible reference mentioned.'
    },
    {
      icon: <Clock className="w-6 h-6" strokeWidth={1.5} />,
      title: 'Key Timestamps',
      description: 'Navigate directly to important moments with labeled timestamps.'
    },
    {
      icon: <Quote className="w-6 h-6" strokeWidth={1.5} />,
      title: 'Notable Quotes',
      description: 'Capture memorable quotes and insights worth remembering.'
    },
    {
      icon: <Lightbulb className="w-6 h-6" strokeWidth={1.5} />,
      title: 'Application Points',
      description: 'Practical takeaways you can apply to your life and ministry.'
    },
    {
      icon: <Play className="w-6 h-6" strokeWidth={1.5} />,
      title: 'Save & Organize',
      description: 'Build your personal library of analyzed sermons for future reference.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-serif text-xl font-bold text-[#1e3a8a]">SermonSift</span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/pricing" className="nav-link">Pricing</Link>
              <Link to="/about" className="nav-link">About</Link>
              {isLoggedIn ? (
                <Link to="/dashboard">
                  <Button className="btn-primary" data-testid="dashboard-btn">Dashboard</Button>
                </Link>
              ) : (
                <div className="flex items-center gap-4">
                  <Link to="/login" className="nav-link">Sign In</Link>
                  <Link to="/signup">
                    <Button className="btn-primary" data-testid="get-started-btn">Get Started</Button>
                  </Link>
                </div>
              )}
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-stone-200 py-4 px-6">
            <div className="flex flex-col gap-4">
              <Link to="/pricing" className="nav-link py-2">Pricing</Link>
              <Link to="/about" className="nav-link py-2">About</Link>
              {isLoggedIn ? (
                <Link to="/dashboard">
                  <Button className="btn-primary w-full">Dashboard</Button>
                </Link>
              ) : (
                <>
                  <Link to="/login" className="nav-link py-2">Sign In</Link>
                  <Link to="/signup">
                    <Button className="btn-primary w-full">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">

            {/* Left: message + CTA */}
            <div className="animate-fade-in">
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1e3a8a] mb-6 leading-tight">
                Study smarter. Preach stronger. Disciple all week.
              </h1>
              <p className="text-lg text-stone-600 mb-8 max-w-xl">
                Analyse any YouTube sermon for instant structure and insight. Then turn your own message into home group guides, devotionals, and social content — automatically.
              </p>

              {/* Key message breakdown */}
              <ul className="space-y-3 mb-10">
                {[
                  'Paste a sermon URL, get a full structured breakdown in under a minute',
                  'Every scripture reference, quote, and timestamp indexed automatically',
                  'Turn your own message into home group guides, devotionals, and social posts',
                ].map((point, i) => (
                  <li key={i} className="flex items-start gap-3 text-stone-700">
                    <Check className="w-5 h-5 text-[#0ea5e9] flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>

              {/* Search Bar */}
              <form onSubmit={handleAnalyze} className="max-w-2xl" data-testid="sermon-analyze-form">
                <div className="flex flex-col sm:flex-row gap-4">
                  <Input
                    type="text"
                    placeholder="Paste your URL here"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    className="input-search flex-1"
                    data-testid="youtube-url-input"
                  />
                  <Button
                    type="submit"
                    className="btn-primary whitespace-nowrap"
                    disabled={isLoading}
                    data-testid="analyze-btn"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Analyzing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Analyze Sermon
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                </div>
              </form>

              <p className="text-xs text-stone-400 mt-3 flex items-center gap-1.5">
                <span>⚠</span>
                Sermon only — not the full service. Videos must be under 60 minutes.
              </p>
              <p className="text-sm text-stone-500 mt-3">
                Free for 48 hours, then $9.99/month. Cancel anytime before your trial ends and you won't be charged.{' '}
                <Link to="/pricing" className="text-[#0ea5e9] hover:underline">See all plans</Link>
              </p>
              <p className="text-sm text-stone-400 mt-2">
                Not sure what you'll get?{' '}
                <Link to="/demo" className="text-[#1e3a8a] hover:underline font-medium">
                  See a sample Spurgeon analysis →
                </Link>
              </p>
            </div>

            {/* Right: founder photo */}
            <div className="animate-fade-in flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#1e3a8a]/10 to-[#0ea5e9]/10 rounded-[2rem] -z-10"></div>
                <img
                  src={jonathanPhoto}
                  alt="Pastor Jonathan Ogle, founder of SermonSift"
                  className="w-full max-w-sm rounded-3xl border border-stone-200 shadow-xl object-cover aspect-[4/5]"
                />
                <div className="mt-4 text-center lg:text-left">
                  <p className="font-serif font-bold text-[#1e3a8a]">Pastor Jonathan Ogle</p>
                  <p className="text-sm text-stone-500">Founder, SermonSift</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative background */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#1e3a8a]/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#0ea5e9]/5 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1e3a8a] mb-4">
              Everything you need for sermon study
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Our AI analyzes sermon transcripts to extract the insights that matter most to pastors and ministry leaders.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="card p-6 stagger-children"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="feature-icon mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-[#1f2937] mb-2">{feature.title}</h3>
                <p className="text-stone-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1e3a8a] mb-4">
              How it works
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Three simple steps to unlock insights from any sermon
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Paste URL', description: 'Copy any YouTube sermon URL and paste it into the search bar.' },
              { step: '2', title: 'AI Analyzes', description: 'Our AI processes the transcript and extracts key insights.' },
              { step: '3', title: 'Get Insights', description: 'View and save your structured analysis for future reference.' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#1e3a8a] text-white flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-[#1f2937] mb-2">{item.title}</h3>
                <p className="text-stone-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1e3a8a] mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-stone-600">
              Try it free, then $9.99/month
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Trial */}
            <div className="pricing-card">
              <h3 className="text-xl font-semibold text-[#1f2937] mb-2">Free Trial</h3>
              <div className="mb-2">
                <span className="text-4xl font-bold text-[#1e3a8a]">$0</span>
                <span className="text-stone-500">/48 hours</span>
              </div>
              <p className="text-xs text-stone-400 mb-6">Card required, then $9.99/month — cancel anytime before your trial ends</p>
              <ul className="space-y-3 mb-8">
                {['Unlimited sermon analyses', 'AI-powered summaries', 'Scripture detection', 'Key timestamps'].map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-stone-600">
                    <Check className="w-5 h-5 text-[#0ea5e9]" strokeWidth={1.5} />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link to="/signup">
                <Button className="btn-secondary w-full" data-testid="free-plan-btn">Start Free Trial</Button>
              </Link>
            </div>

            {/* Pastor Pro */}
            <div className="pricing-card featured">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1e3a8a] text-white text-sm font-medium px-4 py-1 rounded-full">
                Most Popular
              </div>
              <h3 className="text-xl font-semibold text-[#1f2937] mb-2">Pastor Pro</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-[#1e3a8a]">$9.99</span>
                <span className="text-stone-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['Unlimited sermon analyses', 'Clean exports — no watermark', 'Compare up to 3 sermons', 'Shareable analysis links', 'Annual plan saves 34%'].map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-stone-600">
                    <Check className="w-5 h-5 text-[#0ea5e9]" strokeWidth={1.5} />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link to="/pricing">
                <Button className="btn-primary w-full" data-testid="pro-plan-btn">See All Plans</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-[#1e3a8a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to transform your sermon study?
          </h2>
          <p className="text-lg text-blue-200 mb-8 max-w-2xl mx-auto">
            Join pastors and ministry leaders who are saving hours every week with SermonSift.
          </p>
          <Link to="/signup">
            <Button className="bg-white text-[#1e3a8a] hover:bg-stone-100 rounded-full px-8 py-3 font-medium shadow-lg" data-testid="cta-signup-btn">
              Start Your Free Account
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#fafaf9] border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <span className="font-serif text-lg font-bold text-[#1e3a8a]">SermonSift</span>
              <p className="text-sm text-stone-400 mt-1">A Bethany Labs product</p>
            </div>
            <div className="flex items-center gap-6 text-sm text-stone-500">
              <Link to="/pricing" className="hover:text-[#1e3a8a]">Pricing</Link>
              <span>•</span>
              <span>© {new Date().getFullYear()} Bethany Labs. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
